import { useParams } from "react-router-dom";
import { useRouteContext } from "../../../../context/RouteContext";
import FormView from "./FormView";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";
import PrinterHelper from "../../../../helpers/PrinterHelper";
import { useLoadContext } from "../../../../context/LoadContext";

function Form({
  printerData,
  fields,
  images,
  setPrinterData,
  submitError,
  setSubmitError,
  printerDevices,
}) {
  const loadContext = useLoadContext();
  const routeContext = useRouteContext();
  const { printer } = useParams();
  const callEvaApi = useEvaApi();

  /**
   * This method will handle the name change
   * @param {event} e
   */
  function onNameChange(e) {
    setPrinterData((prev) => ({
      ...prev,
      name: e.target.value,
    }));
  }

  /**
   * This method will handle the DPI change
   * @param {number} value
   */
  function onDpiChange(value) {
    setPrinterData((prev) => ({
      ...prev,
      dpi: value,
    }));
  }

  /**
   * This method will handle the printer device change
   * @param {string} value
   */
  function onPrinterDeviceChange(value) {
    setPrinterData((prev) => ({
      ...prev,
      printer_device_id: value,
    }));
  }

  /**
   * This method will handle the rotate change
   * @param {bool} value
   */
  function onRotateChange(value) {
    setPrinterData((prev) => ({
      ...prev,
      rotate: value,
    }));
  }

  /**
   * This method will handle the cancel event
   */
  function onCancel() {
    routeContext.back();
  }

  /**
   * This method will handle the submit event
   */
  async function onSubmit() {
    await callEvaApi(
      new EvaApiCall(printer ? `printers/${printer}` : "printers")
        .setMethod(printer ? "PUT" : "POST")
        .setLoadingGroup("printer_page_submit")
        .setData(printerData)
        .setErrorState(setSubmitError)
        .setAlertError(true)
        .setRedirectOnSuccess(true),
    );
  }

  /**
   * This method will handle the print action.
   */
  async function onPrint() {
    const loadTag = loadContext.enableLoader("printer_page_print");

    try {
      //Get the element to print
      const element = document.getElementById("[PRINT_LABEL_READONLY]");

      // NEW PRINTING METHOD
      if (printer && printerData.printer_device_id) {
        const pdf = await PrinterHelper.getPdfFromHtml(
          element,
          printerData.label_width,
          printerData.label_height,
          printerData.rotate,
        );

        await callEvaApi(
          new EvaApiCall(
            `printer_devices/${printerData.printer_device_id}/print`,
          )
            .setMethod("POST")
            .setData({
              file: pdf.output,
              width: pdf.pdfWidth,
              height: pdf.pdfHeight,
              dpi: printerData.dpi,
            })
            .setAlertSuccess(true)
            .setAlertError(true),
        );
      }

      // LEGACY PRINTING METHOD
      else {
        const zplCode = await PrinterHelper.getZplFromHtml(element);
        await PrinterHelper.printZpl(zplCode);
      }
    } finally {
      loadContext.disableLoader(loadTag);
    }
  }

  return (
    <FormView
      printerData={printerData}
      fields={fields}
      images={images}
      printerDevices={printerDevices}
      onNameChange={onNameChange}
      onDpiChange={onDpiChange}
      onRotateChange={onRotateChange}
      onPrinterDeviceChange={onPrinterDeviceChange}
      onCancel={onCancel}
      onSubmit={onSubmit}
      onPrint={onPrint}
      submitError={submitError}
    />
  );
}

export default Form;
